import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { DashboardService, fmtMoeda, fmtData, mesAtual } from '../services';

export default function Dashboard() {
  const [dados, setDados] = useState(null);
  const [mes, setMes] = useState(mesAtual());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    DashboardService.dados(mes)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false));
  }, [mes]);

  const saldoMes = dados ? dados.receitas_mes - dados.despesas_mes : 0;

  return (
    <Layout title="Dashboard">
      {/* Filtro de mês */}
      <div className="flex-between mb-3">
        <p style={{ color: 'var(--gray-500)', fontSize: 13 }}>Visão geral do período</p>
        <div className="month-picker">
          <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>Período:</span>
          <input type="month" value={mes} onChange={e => setMes(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="loading-wrap"><div className="spinner" /></div>
      ) : !dados ? (
        <div className="empty-state"><p>Erro ao carregar dados</p></div>
      ) : (
        <>
          {/* Cards de resumo */}
          <div className="stat-grid">
            <div className="stat-card saldo">
              <span className="stat-icon">💰</span>
              <span className="stat-label">Saldo Disponível</span>
              <span className="stat-value">{fmtMoeda(dados.saldo_disponivel)}</span>
            </div>
            <div className="stat-card investimento">
              <span className="stat-icon">📈</span>
              <span className="stat-label">Em Investimentos</span>
              <span className="stat-value">{fmtMoeda(dados.total_investimentos)}</span>
            </div>
            <div className="stat-card receita">
              <span className="stat-icon">⬆️</span>
              <span className="stat-label">Receitas do Mês</span>
              <span className="stat-value">{fmtMoeda(dados.receitas_mes)}</span>
            </div>
            <div className="stat-card despesa">
              <span className="stat-icon">⬇️</span>
              <span className="stat-label">Despesas do Mês</span>
              <span className="stat-value">{fmtMoeda(dados.despesas_mes)}</span>
            </div>
            <div className={`stat-card ${saldoMes >= 0 ? 'receita' : 'despesa'}`}>
              <span className="stat-icon">{saldoMes >= 0 ? '✅' : '⚠️'}</span>
              <span className="stat-label">Resultado do Mês</span>
              <span className="stat-value">{fmtMoeda(Math.abs(saldoMes))}</span>
              {saldoMes < 0 && <span className="text-sm text-muted">déficit</span>}
            </div>
          </div>

          <div className="grid-2" style={{ marginBottom: 20 }}>
            {/* Contas */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">🏦 Minhas Contas</span>
              </div>
              <div style={{ padding: '10px 0' }}>
                {dados.contas.length === 0 ? (
                  <div className="empty-state" style={{ padding: 20 }}><p>Nenhuma conta cadastrada</p></div>
                ) : dados.contas.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: c.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                        {c.icone || '🏦'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{c.nome}</div>
                        <div className="text-sm text-muted" style={{ textTransform: 'capitalize' }}>{c.tipo.replace('_', ' ')}</div>
                      </div>
                    </div>
                    <span style={{ fontWeight: 600, color: parseFloat(c.saldo_atual) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {fmtMoeda(c.saldo_atual)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Últimas transações */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">📋 Últimas Transações</span>
              </div>
              <div style={{ padding: '10px 0' }}>
                {dados.ultimas_transacoes.length === 0 ? (
                  <div className="empty-state" style={{ padding: 20 }}><p>Nenhuma transação</p></div>
                ) : dados.ultimas_transacoes.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18 }}>{t.categoria_icone || (t.tipo === 'receita' ? '⬆️' : '⬇️')}</span>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{t.descricao}</div>
                        <div className="text-sm text-muted">{fmtData(t.data)} · {t.categoria_nome || '—'}</div>
                      </div>
                    </div>
                    <span className={t.tipo === 'receita' ? 'value-positive' : 'value-negative'}>
                      {t.tipo === 'receita' ? '+' : '-'}{fmtMoeda(t.valor)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid-2" style={{ marginBottom: 20 }}>
            {/* Gastos por categoria */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">🏷️ Gastos por Categoria</span>
              </div>
              <div className="card-body">
                {dados.gastos_por_categoria.length === 0 ? (
                  <div className="empty-state" style={{ padding: 10 }}><p>Sem despesas no período</p></div>
                ) : dados.gastos_por_categoria.map((g, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <div className="flex-between mb-1">
                      <span style={{ fontSize: 13 }}>{g.icone} {g.nome}</span>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{fmtMoeda(g.total)}</span>
                    </div>
                    <div className="progress-bar-wrap">
                      <div className="progress-bar-fill"
                        style={{ width: `${Math.min(100, (g.total / dados.despesas_mes) * 100)}%`, background: g.cor || '#dc2626' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Metas */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">🎯 Metas Ativas</span>
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {dados.metas_ativas.length === 0 ? (
                  <div className="empty-state" style={{ padding: 10 }}><p>Nenhuma meta ativa</p></div>
                ) : dados.metas_ativas.map(m => {
                  const pct = Math.min(100, (m.valor_atual / m.valor_objetivo) * 100);
                  return (
                    <div key={m.id}>
                      <div className="flex-between mb-1">
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{m.icone} {m.nome}</span>
                        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{pct.toFixed(0)}%</span>
                      </div>
                      <div className="progress-bar-wrap">
                        <div className="progress-bar-fill" style={{ width: `${pct}%`, background: m.cor || '#16a34a' }} />
                      </div>
                      <div className="flex-between mt-1">
                        <span className="text-sm text-muted">{fmtMoeda(m.valor_atual)}</span>
                        <span className="text-sm text-muted">{fmtMoeda(m.valor_objetivo)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Cartões e Faturas */}
          {dados.cartoes.length > 0 && (
            <div className="card mb-3">
              <div className="card-header">
                <span className="card-title">💳 Cartões de Crédito</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, padding: 20 }}>
                {dados.cartoes.map(c => {
                  const usado = c.limite - c.limite_disponivel;
                  const pct = (usado / c.limite) * 100;
                  return (
                    <div key={c.id} className="credit-card-visual" style={{ background: `linear-gradient(135deg, ${c.cor}, ${c.cor}99)` }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.nome}</div>
                      <div>
                        <div style={{ fontSize: 11, opacity: .8, marginBottom: 4 }}>Limite usado</div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtMoeda(usado)}</div>
                        <div style={{ fontSize: 11, opacity: .7 }}>de {fmtMoeda(c.limite)}</div>
                        <div style={{ marginTop: 10, background: 'rgba(255,255,255,.2)', borderRadius: 100, height: 6 }}>
                          <div style={{ width: `${Math.min(100, pct)}%`, background: pct > 80 ? '#fca5a5' : 'white', height: 6, borderRadius: 100 }} />
                        </div>
                        <div style={{ fontSize: 10, opacity: .7, marginTop: 4 }}>Vence dia {c.dia_vencimento}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Evolução mensal */}
          {dados.evolucao_mensal.length > 0 && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">📊 Evolução dos Últimos 6 Meses</span>
              </div>
              <div className="card-body">
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Mês</th>
                        <th>Receitas</th>
                        <th>Despesas</th>
                        <th>Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.evolucao_mensal.map(e => {
                        const resultado = e.receitas - e.despesas;
                        return (
                          <tr key={e.mes}>
                            <td style={{ fontWeight: 500 }}>{e.mes}</td>
                            <td className="value-positive">{fmtMoeda(e.receitas)}</td>
                            <td className="value-negative">{fmtMoeda(e.despesas)}</td>
                            <td className={resultado >= 0 ? 'value-positive' : 'value-negative'}>
                              {fmtMoeda(resultado)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
